import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Task, TaskStatus } from '../types';
import { getTasks, getTask } from '../services/api';
import TaskCard from '../components/TaskCard';
import TaskDetail from '../components/TaskDetail';
import QuickTaskComposer from '../components/QuickTaskComposer';
import { useTheme } from '../context/ThemeContext';
import InfoModal from '../components/InfoModal';
import HistorySearchBar, { TimeRange, TIME_RANGE_TO_OPTIONS } from '../components/HistorySearchBar';

const QuickTasks: React.FC = () => {
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const taskIdParam = searchParams.get('task');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all');
  const [loading, setLoading] = useState(true);
  // True once the FIRST load has come back. The full-page "Loading..." below is gated on this
  // rather than on `tasks.length === 0`, because that older test unmounted the whole page (and
  // with it the search box) on every refetch that happened to have no results yet: type a query
  // matching nothing, and the next keystroke blanked the page and took the cursor with it. That
  // is the focus loss people reported on Backspace; debouncing the fetch made it rarer, not gone.
  // The same blanking hit the 10s auto-refresh whenever the list was legitimately empty.
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [search, setSearch] = useState('');
  // Debounced copy of `search`: the input itself stays bound to the raw `search`
  // state so typing feels instant, but data-fetching waits for the user to pause
  // so every keystroke doesn't trigger a loadTasks() re-render (which was
  // stealing focus from the input mid-keystroke).
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [timeRange, setTimeRange] = useState<TimeRange>('30');

  // Create modal is the shared QuickTaskComposer.
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, debouncedSearch, timeRange]);

  // Calculate running count from actual tasks
  const runningCount = tasks.filter(t => t.status === TaskStatus.IN_PROGRESS).length;

  // Auto-refresh every 10 seconds if there are running executions
  useEffect(() => {
    if (runningCount > 0) {
      const interval = setInterval(() => {
        loadTasks();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [runningCount]);

  // Load task from URL parameter if present
  useEffect(() => {
    const loadTaskFromParam = async () => {
      if (taskIdParam) {
        try {
          const task = await getTask(parseInt(taskIdParam, 10));
          setSelectedTask(task);
        } catch (error) {
          console.error('Failed to load task:', error);
        }
      }
    };
    loadTaskFromParam();
  }, [taskIdParam]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const filter = statusFilter === 'all' ? undefined : statusFilter;
      const taskList = await getTasks(undefined, filter, undefined, undefined, true, {
        search: debouncedSearch.trim() || undefined,
        ...TIME_RANGE_TO_OPTIONS(timeRange),
      });
      setTasks(taskList);
    } catch (error) {
      console.error('Error loading quicktasks:', error);
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  };

  const handleTaskUpdate = () => {
    loadTasks();
  };

  const handleTaskSelect = (task: Task) => {
    setSelectedTask(task);
    setSearchParams({ task: task.id.toString() });
  };

  const handleTaskClose = () => {
    setSelectedTask(null);
    setSearchParams({});
  };

  if (loading && !initialLoadDone) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px', overflowX: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ color: theme.text, margin: 0 }}>Quick Tasks</h1>
            <InfoModal
              title="Quick Tasks (Frontend QA)"
              body={[
                "Fire-and-watch one-off prompts to the dev server — great for quick fixes and checks.",
                "This is where Frontend QA lives: open the running app, and when you spot a defect, file a QuickTask describing it. The AI fixes it; you re-check.",
                "Each task streams live so you can watch the AI work.",
              ]}
            />
          </div>
          {runningCount > 0 && (
            <div style={{
              color: theme.primary,
              fontSize: '14px',
              marginTop: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: theme.primary,
                animation: 'pulse 1.5s infinite'
              }} />
              {runningCount} task{runningCount > 1 ? 's' : ''} running...
              <style>{`
                @keyframes pulse {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.4; }
                }
              `}</style>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={loadTasks}
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.surface,
              color: theme.text,
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: theme.primary,
              color: '#fff',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span style={{ fontSize: '20px' }}>+</span>
            Create Quick Task
          </button>
        </div>
      </div>

      <p style={{ color: theme.textSecondary, marginBottom: '24px' }}>
        Quick tasks execute the /quicktask skill for rapid fixes. Submit multiple tasks - they run in parallel.
      </p>

      {/* Search + time range */}
      <div style={{ marginBottom: '16px' }}>
        <HistorySearchBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search title or description..."
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
        />
      </div>

      {/* Status Filter */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setStatusFilter('all')}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: `1px solid ${theme.border}`,
            backgroundColor: statusFilter === 'all' ? theme.primary : theme.surface,
            color: statusFilter === 'all' ? '#fff' : theme.text,
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: statusFilter === 'all' ? '600' : '400'
          }}
        >
          All ({tasks.length})
        </button>
        {Object.values(TaskStatus).map(status => {
          const count = tasks.filter(t => t.status === status).length;
          return (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: `1px solid ${theme.border}`,
                backgroundColor: statusFilter === status ? theme.primary : theme.surface,
                color: statusFilter === status ? '#fff' : theme.text,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: statusFilter === status ? '600' : '400'
              }}
            >
              {status.replace('_', ' ').toUpperCase()} ({count})
            </button>
          );
        })}
      </div>

      {/* Task List */}
      {tasks.length === 0 ? (
        <div style={{
          padding: '48px',
          textAlign: 'center',
          color: theme.textSecondary,
          backgroundColor: theme.surface,
          borderRadius: '8px',
          border: `1px solid ${theme.border}`
        }}>
          <p style={{ fontSize: '18px', marginBottom: '8px' }}>No quick tasks found</p>
          <p style={{ fontSize: '14px' }}>
            {search || timeRange !== '30'
              ? 'Try a different search term or widen the time range.'
              : 'Click "Create Quick Task" above to create one.'}
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: '16px',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(350px, 100%), 1fr))'
        }}>
          {tasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={() => handleTaskSelect(task)}
              isSelected={selectedTask?.id === task.id}
            />
          ))}
        </div>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetail
          task={selectedTask}
          onClose={handleTaskClose}
          onTaskUpdate={handleTaskUpdate}
        />
      )}

      {/* Create Quick Task: shared composer (same one the header + uses).
          Stays open so several can be fired in a row; each lands at the top. */}
      <QuickTaskComposer
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmitted={(newTask) => setTasks(prev => [newTask, ...prev])}
        keepOpenAfterSubmit
        showRecentSubmissions
      />
    </div>
  );
};

export default QuickTasks;
